import { initializeApp, getApps } from 'firebase/app';
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
} from 'firebase/firestore';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import { printerApi } from '../../api';
import { firebaseConfig } from '../../firebase';

const JOBS_COLLECTION = 'printer_jobs';
const SECONDARY_APP_NAME = 'trackprofit-printer-host';

const decodePayload = (payload) => {
  if (!payload) return null;

  const fromBase64 = (value) => {
    const atobImpl =
      typeof atob === 'function'
        ? atob
        : (typeof window !== 'undefined' && typeof window.atob === 'function' ? window.atob : null);
    if (!atobImpl) {
      throw new Error('Base64 decoding is not supported in this environment.');
    }
    const binary = atobImpl(value);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  };

  if (payload.encoding === 'base64' && typeof payload.data === 'string') {
    return fromBase64(payload.data);
  }
  if (Array.isArray(payload)) {
    return Uint8Array.from(payload);
  }
  if (Array.isArray(payload.data)) {
    return Uint8Array.from(payload.data);
  }
  if (payload.type === 'Buffer' && Array.isArray(payload.data)) {
    return Uint8Array.from(payload.data);
  }
  if (payload.data && payload.data.type === 'Buffer' && Array.isArray(payload.data.data)) {
    return Uint8Array.from(payload.data.data);
  }
  return null;
};

export class RemotePrinterHostAgent {
  constructor(options = {}) {
    this.options = options;
    this.supported = typeof window !== 'undefined' && !!window.electronAPI?.isElectron;
    this.state = {
      supported: this.supported,
      enabled: false,
      status: this.supported ? 'idle' : 'unsupported',
      hostId: null,
      deviceId: null,
      deviceName: null,
      printerName: null,
      lastJob: null,
      error: null,
    };
    this.jobQueue = [];
    this.processing = false;
    this.unsubscribeJobs = null;
    this.currentHost = null;
    this.firebaseAuthUser = null;
    this.firestore = null;
    this.beforeUnloadHandler = null;
    this.startPromise = null;
  }

  updateState(patch = {}) {
    this.state = { ...this.state, ...patch };
    if (typeof this.options.onStateChange === 'function') {
      this.options.onStateChange(this.state);
    }
  }

  async setUserContext(user) {
    this.userContext =
      user && user.userId
        ? {
            userId: user.userId,
            name: user.name || null,
            phone: user.phone || null,
          }
        : null;
    if (!this.userContext) {
      await this.stop(true);
      return;
    }
    await this.maybeStart();
  }

  async onPrinterStateChange({ connected }) {
    if (!connected) {
      await this.stop(true);
      return;
    }
    await this.maybeStart();
  }

  attachBeforeUnload() {
    if (this.beforeUnloadHandler || typeof window === 'undefined') return;
    this.beforeUnloadHandler = () => {
      this.stop(true).catch(() => {});
    };
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }

  detachBeforeUnload() {
    if (!this.beforeUnloadHandler || typeof window === 'undefined') return;
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    this.beforeUnloadHandler = null;
  }

  async ensureFirebaseSession() {
    if (this.firestore && this.firebaseAuthUser) {
      return;
    }
    let app = getApps().find((entry) => entry.name === SECONDARY_APP_NAME);
    if (!app) {
      app = initializeApp(firebaseConfig, SECONDARY_APP_NAME);
    }
    const auth = getAuth(app);
    if (!this.firebaseAuthUser) {
      const { token } = await printerApi.createFirebaseToken();
      this.firebaseAuthUser = await signInWithCustomToken(auth, token);
    }
    this.firestore = getFirestore(app);
  }

  async maybeStart() {
    if (!this.supported) return;
    if (!this.userContext?.userId) return;
    if (this.startPromise) {
      await this.startPromise;
      return;
    }
    this.startPromise = this.startHost();
    try {
      await this.startPromise;
    } finally {
      this.startPromise = null;
    }
  }

  async startHost() {
    const device = this.options.getDevice?.();
    if (!device?.id) return;
    const hostIdentity = typeof this.options.getHostIdentity === 'function' ? await this.options.getHostIdentity() : null;
    if (!hostIdentity?.deviceId) return;
    if (this.state.enabled && this.currentHost?.deviceId === hostIdentity.deviceId) {
      return;
    }

    try {
      this.updateState({ status: 'registering', error: null });
      await this.ensureFirebaseSession();
      const metadata = {
        environment: 'electron',
        appVersion: window?.electronAPI?.version || null,
        platform: window?.electronAPI?.platform || navigator?.platform || null,
      };
      const host = await printerApi.registerHost({
        deviceId: hostIdentity.deviceId,
        deviceName: hostIdentity.deviceName || metadata.platform || device.name || hostIdentity.deviceId,
        platform: metadata.platform || 'electron',
        printer: {
          id: device.id,
          name: device.name || device.id,
        },
        metadata,
      });
      this.currentHost = host;
      this.updateState({
        enabled: true,
        status: 'listening',
        hostId: host.hostId,
        deviceId: host.deviceId,
        deviceName: hostIdentity.deviceName || host.deviceName || host.deviceId,
        printerName: host.printer?.name || device.name || 'Printer',
        error: null,
      });
      this.startJobListener();
      this.attachBeforeUnload();
    } catch (error) {
      console.error('[RemotePrinterHostAgent] failed to register host', error);
      this.updateState({
        enabled: false,
        status: 'error',
        error: error?.message || 'Failed to register desktop printer host.',
      });
    }
  }

  startJobListener() {
    if (!this.firestore || !this.currentHost?.hostId) return;
    if (this.unsubscribeJobs) {
      this.unsubscribeJobs();
      this.unsubscribeJobs = null;
    }
    const jobsRef = collection(this.firestore, JOBS_COLLECTION);
    const jobQuery = query(
      jobsRef,
      where('userId', '==', this.userContext.userId),
      where('hostId', '==', this.currentHost.hostId),
      where('status', '==', 'pending'),
      orderBy('createdAt', 'asc'),
      limit(10),
    );
    this.unsubscribeJobs = onSnapshot(
      jobQuery,
      (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === 'added') {
            this.enqueueJob(change.doc.data());
          }
        });
      },
      (error) => {
        console.error('[RemotePrinterHostAgent] Firestore listener error', error);
        this.updateState({
          status: 'error',
          error: error?.message || 'Job listener failed.',
        });
      }
    );
  }

  enqueueJob(job) {
    if (!job?.jobId) return;
    this.jobQueue.push(job);
    this.processQueue();
  }

  async processQueue() {
    if (this.processing) return;
    const job = this.jobQueue.shift();
    if (!job) return;
    this.processing = true;
    try {
      this.updateState({
        status: 'printing',
        lastJob: {
          jobId: job.jobId,
          status: 'printing',
          title: job.title || job.jobId,
        },
      });
      await printerApi.updateJobStatus(job.jobId, {
        status: 'printing',
        deviceId: job.targetDeviceId,
      });
      const payload = decodePayload(job.payload);
      if (!payload) {
        throw new Error('Invalid print payload.');
      }
      const printResult = await this.options.printRaw?.(payload, {
        allowQueue: false,
        printer: job.printer?.name || job.printer?.id,
      });
      if (!printResult?.success) {
        throw new Error(printResult?.message || 'Native print failed.');
      }
      await printerApi.updateJobStatus(job.jobId, {
        status: 'done',
        deviceId: job.targetDeviceId,
      });
      this.updateState({
        status: 'listening',
        lastJob: {
          jobId: job.jobId,
          status: 'done',
          completedAt: Date.now(),
          title: job.title || job.jobId,
        },
        error: null,
      });
    } catch (error) {
      console.error('[RemotePrinterHostAgent] print failed', error);
      try {
        await printerApi.updateJobStatus(job.jobId, {
          status: 'error',
          error: error?.message || 'Print failed.',
          deviceId: job.targetDeviceId,
        });
      } catch (statusError) {
        console.warn('[RemotePrinterHostAgent] failed to update job status', statusError);
      }
      this.updateState({
        status: 'error',
        lastJob: {
          jobId: job.jobId,
          status: 'error',
          message: error?.message || 'Print failed.',
          title: job.title || job.jobId,
        },
        error: error?.message || 'Print failed.',
      });
    } finally {
      this.processing = false;
      if (this.jobQueue.length) {
        this.processQueue();
      } else if (this.state.enabled) {
        this.updateState({ status: 'listening' });
      }
    }
  }

  async stop(markOffline = false) {
    if (this.unsubscribeJobs) {
      this.unsubscribeJobs();
      this.unsubscribeJobs = null;
    }
    this.detachBeforeUnload();
    this.jobQueue = [];
    this.processing = false;
    if (markOffline && this.currentHost?.deviceId) {
      try {
        await printerApi.removeHost(this.currentHost.deviceId);
      } catch (error) {
        console.warn('[RemotePrinterHostAgent] failed to mark host offline', error);
      }
    }
    this.currentHost = null;
    this.updateState({
      enabled: false,
      status: this.supported ? 'idle' : 'unsupported',
      hostId: null,
      deviceId: null,
      deviceName: null,
      printerName: null,
    });
  }
}

